/**
 * 내 메일 서명 패널 — 메일함 사이드바의 "설정 → 내 서명" 진입점.
 *
 * 다중 서명 CRUD + 기본 서명 토글. 편집은 기존 SignatureEditorDialog 재활용.
 */
import React, { useEffect, useState } from "react";
import {
    Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography,
} from "@mui/material";
import AddRoundedIcon           from "@mui/icons-material/AddRounded";
import EditRoundedIcon          from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import StarRoundedIcon          from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon    from "@mui/icons-material/StarBorderRounded";

import { mailApi } from "../../api/mailApi";
import { notify }  from "../../../../util/toast";
import { confirm } from "../../../../util/confirmDialog";
import { ds }      from "../../../../styles/designSystem";

import { toSignaturePreviewHtml } from "./signaturePreview";
import SignatureEditorDialog from "./SignatureEditorDialog";
import ActionButton from "../../../../component/common/ActionButton";
import LoadingDots from "../../../../component/common/LoadingDots";

export default function MySignaturesPanel() {
    const [list, setList]       = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null=닫힘, {}=새 작성, {id,...}=수정

    const reload = async () => {
        setLoading(true);
        try {
            const res = await mailApi.listSignatures();
            const data = res?.data ?? res ?? [];
            setList(Array.isArray(data) ? data : []);
        } catch (e) {
            notify(e?.response?.data?.message || "서명 목록 로드 실패", "error");
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); }, []);

    const handleSetDefault = async (s) => {
        if (s.isDefault) return;
        try {
            await mailApi.setDefaultSignature(s.id);
            notify(`"${s.name}" 을(를) 기본 서명으로 설정`, "success");
            reload();
        } catch (e) {
            notify(e?.response?.data?.message || "기본 서명 설정 실패", "error");
        }
    };

    const handleDelete = async (s) => {
        const ok = await confirm({
            title: "서명 삭제",
            message: `"${s.name}" 서명을 삭제할까요?`,
            confirmLabel: "삭제",
            variant: "danger",
        });
        if (!ok) return;
        try {
            await mailApi.deleteSignature(s.id);
            notify("삭제되었습니다", "success");
            reload();
        } catch (e) {
            notify(e?.response?.data?.message || "삭제 실패", "error");
        }
    };

    return (
        <Box sx={{ p: 3, height: "100%", overflowY: "auto" }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
                        내 서명
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>
                        기본 서명은 새 메일·답장·전달 시 자동 삽입됩니다.
                    </Typography>
                </Box>
                <ActionButton variant="add" onClick={() => setEditing({})} label="새 서명" />
            </Stack>

            {loading ? (
                <LoadingDots/>
            ) : list.length === 0 ? (
                <Box sx={{
                    p: 4, textAlign: "center", border: "1px dashed #e2e8f0",
                    borderRadius: 1.5, color: "#94a3b8",
                }}>
                    <Typography sx={{ fontSize: 13 }}>등록된 서명이 없습니다.</Typography>
                    <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                        "새 서명" 버튼을 눌러 첫 서명을 만들어 보세요.
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={1.2}>
                    {list.map(s => (
                        <Paper key={s.id} variant="outlined" sx={{
                            p: 1.5, borderRadius: 1.5,
                            borderColor: s.isDefault ? ds.colors.primary : "#e2e8f0",
                        }}>
                            {/* 머리줄 — 이름·배지·동작. 미리보기는 아래 한 줄을 통째로 쓴다.
                                (예전엔 이 행 안에 미리보기가 끼어 있어 명함형 서명이 아이콘 사이
                                 좁은 칸에 갇혔다.) */}
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Tooltip title={s.isDefault ? "기본 서명" : "기본으로 설정"}>
                                    <IconButton size="small" onClick={() => handleSetDefault(s)}
                                        sx={{ color: s.isDefault ? "#f59e0b" : "#94a3b8" }}>
                                        {s.isDefault
                                            ? <StarRoundedIcon fontSize="small" />
                                            : <StarBorderRoundedIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                                <Stack direction="row" alignItems="center" spacing={1}
                                    sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>
                                        {s.name || "(이름 없음)"}
                                    </Typography>
                                    {s.isDefault && (
                                        <Chip size="small" label="기본"
                                            sx={{
                                                height: 18, fontSize: 10, fontWeight: 700,
                                                bgcolor: "rgba(37,99,235,0.1)", color: ds.colors.primary,
                                            }} />
                                    )}
                                    {/* 작성 방식 — 편집을 열기 전에 어느 탭으로 열릴지 미리 알 수 있게. */}
                                    <Chip size="small" label={s.bodyType === "TEXT" ? "기본 텍스트" : "HTML"}
                                        sx={{
                                            height: 18, fontSize: 10, fontWeight: 700,
                                            bgcolor: "#f1f5f9", color: "#64748b",
                                        }} />
                                </Stack>
                                <Tooltip title="수정">
                                    <IconButton size="small" onClick={() => setEditing(s)}>
                                        <EditRoundedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="삭제">
                                    <IconButton size="small" onClick={() => handleDelete(s)} sx={{ color: "#dc2626" }}>
                                        <DeleteOutlineRoundedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>

                            {/* 미리보기 — 실제 메일에 박힐 모습 그대로. 앞뒤 빈 줄은 걷어내고 보여 준다
                                (안 그러면 카드 높이를 빈 <p><br></p> 가 다 먹어 흰 화면이 된다).
                                서명 표는 대개 450px 고정이라 카드 폭 안에 그대로 들어간다. */}
                            <PreviewBody html={toSignaturePreviewHtml(s.bodyHtml)} />
                        </Paper>
                    ))}
                </Stack>
            )}

            <SignatureEditorDialog
                open={editing != null}
                initial={editing}
                onClose={() => setEditing(null)}
                onSaved={() => { setEditing(null); reload(); }}
            />
        </Box>
    );
}

/**
 * 카드 아래쪽 미리보기 상자.
 *
 * <p>흰 바탕에 얹는 이유 — 서명은 흰 메일 본문 위에 놓이는 물건이라, 회색 바탕에서 보면
 * 실제와 색이 달라 보인다. 표 테두리가 붙은 명함형 서명일수록 차이가 크다.
 *
 * <p>{@code maxHeight} 를 넘어가면 스크롤로 마저 볼 수 있게 둔다. 잘라 버리면 긴 서명은
 * 목록에서 아랫부분을 영영 확인할 수 없다.
 */
function PreviewBody({ html }) {
    if (!html) {
        return (
            <Typography sx={{ mt: 1, ml: 5, fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>
                (내용이 비어 있습니다)
            </Typography>
        );
    }
    return (
        <Box
            sx={{
                mt: 1, ml: 5, p: 1.5,
                border: "1px solid #e2e8f0", borderRadius: 1, bgcolor: "#fff",
                fontSize: 13, color: "#334155", lineHeight: 1.6,
                maxHeight: 260, overflow: "auto",
                // 단락 여백은 건드리지 않는다 — 실제 메일에서 보이는 간격 그대로 보여야 한다.
                "& img": { maxWidth: "100%" },
                "& table": { maxWidth: "100%" },
            }}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}
